import { useCallback, useState } from 'react'

import {
  type Access,
  type IssuedAccess,
  type Use,
  changeAccess,
  fetchAssistantAccess,
  issueAccess,
  revokeAccess,
} from '../api/assistants'
import { asFailure, type Failure } from '../api/request'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Failed, Waiting } from '../ui/Status'
import { Switch } from '../ui/Switch'
import { Allow } from './assistants/Allow'
import { Issued } from './assistants/Issued'

/** The area's own id, so the address is written once. */
export const ASSISTANTS_AREA = 'assistants'

/**
 * Which assistants can reach this space, and what they have been doing.
 *
 * Two things are on this screen, and the first of them is the address. It carries the space, it is
 * the first thing any client asks for, and it is answered by the service rather than assembled here
 * — what a space's address is must not be something the browser decides. Copying it is one control,
 * because on a phone selecting ninety characters by hand is not a thing anybody does twice.
 *
 * The second is the list. Each access says **where its token went** — connected by a program, or
 * made here and copied out — because that is what somebody needs in order to recognise one months
 * later, and it is the one difference between the two ways of arriving. Whether it may write is a
 * switch on the access rather than a setting of the installation: one assistant is trusted, the next
 * is being tried out.
 *
 * **What was done through an access is on the access**, folded away until asked for. It is names and
 * moments, never what was searched for — enough to notice something that was not you, and far too
 * little to be a second copy of somebody's thinking.
 *
 * Revoking asks first, and that is deliberate in a codebase where ordinary actions do not. It is not
 * undoable and the assistant stops mid-conversation; the confirmation says which one and what
 * happens, and it says that nothing the assistant wrote goes with it — which is the thing somebody
 * is actually worried about.
 *
 * The area is reached at `#/assistants`, from the account panel, and stands in no navigation:
 * connecting an assistant is something somebody does once and then forgets about. The consent page
 * lives under the same area at `#/assistants/allow/<request>`, because it is the same subject and
 * because a person arriving there may have to sign in first — which the frame already handles for
 * every address inside the application.
 */
export function Assistants() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const consenting = route.path[0] === 'allow' ? route.path[1] : undefined

  const area = useLoad(useCallback((signal: AbortSignal) => fetchAssistantAccess(signal), []))
  const announce = useAnnounce()

  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newMayWrite, setNewMayWrite] = useState(false)
  const [issued, setIssued] = useState<IssuedAccess | null>(null)
  const [asking, setAsking] = useState<string | null>(null)
  const [opened, setOpened] = useState<string | null>(null)
  const [working, setWorking] = useState(false)
  const [refused, setRefused] = useState<Failure | null>(null)

  if (consenting !== undefined) {
    return (
      <Lanes
        overview={{
          label: 'Connect an assistant',
          content: (
            <Allow
              request={consenting}
              onDone={() => {
                navigate(ASSISTANTS_AREA, [])
              }}
            />
          ),
        }}
      />
    )
  }

  if (area.load.status === 'waiting') {
    return <Waiting label="Looking at what can reach this space" />
  }
  if (area.load.status === 'failed') {
    return <Failed failure={area.load.failure} onRetry={area.retry} />
  }

  const { endpoint, mount, accesses } = area.load.value

  const issue = async () => {
    setWorking(true)
    setRefused(null)
    try {
      const made = await issueAccess(newName, newMayWrite)
      setIssued(made)
      setAdding(false)
      setNewName('')
      setNewMayWrite(false)
      announce(`${made.access.name} can now reach this space.`)
      area.retry()
    } catch (failure) {
      setRefused(asFailure(failure))
    } finally {
      setWorking(false)
    }
  }

  const setWrites = async (access: Access, mayWrite: boolean) => {
    setRefused(null)
    try {
      await changeAccess(access.id, { mayWrite })
      announce(mayWrite ? `${access.name} may now write.` : `${access.name} can now only read.`)
      area.retry()
    } catch (failure) {
      setRefused(asFailure(failure))
    }
  }

  const revoke = async (access: Access) => {
    setWorking(true)
    setRefused(null)
    try {
      await revokeAccess(access.id)
      setAsking(null)
      announce(`${access.name} has been revoked.`)
      area.retry()
    } catch (failure) {
      setRefused(asFailure(failure))
    } finally {
      setWorking(false)
    }
  }

  return (
    <Lanes
      overview={{
        label: 'Assistants',
        content: (
          <div className="assistants">
            <header className="assistants__head">
              <h2 className="assistants__title">Assistants</h2>
              <p className="assistants__lead">
                An assistant can search this space, read what is in it, and — if you allow it —
                write here too. It sees exactly what you see.
              </p>
            </header>

            <Address
              id="assistant-endpoint"
              label="This space’s address"
              address={endpoint}
              hint="Give this to an assistant as a custom connector. It carries this space, so a second space would have an address of its own."
            />

            <Address
              id="assistant-mount"
              label="This space’s library as a drive"
              address={mount}
              hint="Connect this in a WebDAV client on your iPad, or in the Finder, and the library appears as a folder you can open files from. The user name does not matter; the password is a token from below."
            />

            {refused !== null && <Failed failure={refused} title="That did not work" />}

            {issued !== null && (
              <Issued
                name={issued.access.name}
                token={issued.tokenShownOnlyNow}
                endpoint={endpoint}
                onDone={() => {
                  setIssued(null)
                }}
              />
            )}

            {accesses.length === 0 && !adding && (
              <Offer
                title="Nothing can reach this space yet"
                lead="Connect an assistant and it can work from your own material instead of from nothing."
                points={[
                  'In the assistant, add MyLib as a connector and give it the address above.',
                  'You decide here whether it may only read or also write.',
                  'Revoke it whenever you like — nothing it wrote goes with it.',
                ]}
                action={
                  <Button
                    tone="primary"
                    onClick={() => {
                      setAdding(true)
                    }}
                  >
                    Make an access by hand
                  </Button>
                }
                note="Only needed for assistants that cannot open a browser — a terminal, an editor."
              />
            )}

            {accesses.length > 0 && (
              <ul className="assistants__list">
                {accesses.map((access) => (
                  <li className="access" key={access.id}>
                    <div className="access__what">
                      <h3 className="access__name">{access.name}</h3>
                      <p className="access__where">
                        {whereItCameFrom(access)} · {whenItWasUsed(access)}
                      </p>
                    </div>

                    <Switch
                      label="May also write"
                      on={access.mayWrite}
                      onChange={(on) => {
                        void setWrites(access, on)
                      }}
                    />

                    {access.uses.length > 0 && (
                      <details
                        className="access__uses"
                        open={opened === access.id}
                        onToggle={(event) => {
                          setOpened(event.currentTarget.open ? access.id : null)
                        }}
                      >
                        <summary>What it did, most recently</summary>
                        <ul className="uses">
                          {access.uses.map((use, at) => (
                            <li className="uses__entry" key={`${use.at}-${at}`}>
                              <span className="uses__when">{when(use.at)}</span>
                              <span className="uses__what">{inWords(use)}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    {asking === access.id ? (
                      <div className="access__asking" role="group">
                        <p>
                          Revoke {access.name}? It stops working at once. Everything it wrote stays
                          where it is.
                        </p>
                        <div className="controls">
                          <Button
                            tone="primary"
                            disabled={working}
                            onClick={() => {
                              void revoke(access)
                            }}
                          >
                            Revoke
                          </Button>
                          <Button
                            onClick={() => {
                              setAsking(null)
                            }}
                          >
                            Keep it
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="controls">
                        <Button
                          onClick={() => {
                            setAsking(access.id)
                          }}
                        >
                          Revoke
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {adding ? (
              <div className="access-form">
                <h3 className="access-form__title">An access by hand</h3>
                <p className="access-form__note">
                  You will see the token once, now, and never again. For assistants that cannot open
                  a browser to ask for themselves.
                </p>
                <div className="field">
                  <label className="field__label" htmlFor="new-access-name">
                    Call it
                  </label>
                  <input
                    id="new-access-name"
                    className="field__control"
                    value={newName}
                    maxLength={120}
                    placeholder="Claude in the terminal"
                    onChange={(event) => {
                      setNewName(event.target.value)
                    }}
                  />
                </div>
                <Switch
                  label="May also write"
                  hint="Off, it can only read."
                  on={newMayWrite}
                  onChange={setNewMayWrite}
                  disabled={working}
                />
                <div className="controls">
                  <Button
                    tone="primary"
                    disabled={working || newName.trim() === ''}
                    onClick={() => {
                      void issue()
                    }}
                  >
                    Make it
                  </Button>
                  <Button
                    disabled={working}
                    onClick={() => {
                      setAdding(false)
                      setRefused(null)
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              accesses.length > 0 && (
                <div className="controls">
                  <Button
                    onClick={() => {
                      setAdding(true)
                    }}
                  >
                    Make an access by hand
                  </Button>
                </div>
              )
            )}
          </div>
        ),
      }}
    />
  )
}

/**
 * One address of this space, and a control to copy it.
 *
 * Two of these stand on the screen — the one an assistant is given and the one a file manager
 * mounts — because they are two protocols over the same key. One component rather than two, so the
 * second address cannot quietly drift into looking like a different kind of thing.
 */
function Address({
  id,
  label,
  address,
  hint,
}: {
  readonly id: string
  readonly label: string
  readonly address: string
  readonly hint: string
}) {
  const [copied, setCopied] = useState(false)

  return (
    <div className="endpoint">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="endpoint__value">
        <input id={id} className="endpoint__text" readOnly value={address} />
        <Button
          onClick={() => {
            void navigator.clipboard
              .writeText(address)
              .then(() => {
                setCopied(true)
              })
              .catch(() => {
                setCopied(false)
              })
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="field__hint">{hint}</p>
    </div>
  )
}

function whereItCameFrom(access: Access): string {
  if (access.origin === 'connected') {
    return access.clientName === '' ? 'Connected itself' : `Connected by ${access.clientName}`
  }
  return 'Made here, token copied out'
}

function whenItWasUsed(access: Access): string {
  return access.lastUsedAt === null ? 'never used' : `last used ${when(access.lastUsedAt)}`
}

/**
 * A tool name as something to read.
 *
 * The names are written for an assistant to choose from, not for somebody scanning a list at
 * midnight wondering whether that was them. Anything unrecognised is shown as it arrived — a name
 * nobody knows is exactly the entry worth seeing.
 */
function inWords(use: Use): string {
  const said = SAID[use.tool] ?? use.tool
  return use.outcome === 'answered' ? said : `${said} — ${use.outcome}`
}

const SAID: Readonly<Record<string, string>> = {
  search: 'searched',
  read_content: 'read something',
  list_folders: 'looked at the folders',
  list_documents: 'listed documents',
  write_document: 'wrote a document',
  upload_file: 'put a file in',
  organise_document: 'moved or renamed a document',
  organise_folder: 'changed a folder',
  list_notes: 'looked at the notepad',
  write_note: 'wrote a note',
  organise_note: 'struck out a note',
  list_tasks: 'looked at the tasks',
  create_task: 'wrote down a task',
  update_task: 'changed a task',
  remove_task: 'removed a task',
  list_flashcards: 'looked at the flashcards',
  create_flashcard: 'made a flashcard',
  update_flashcard: 'changed a flashcard',
  remove_flashcard: 'removed a flashcard',
  list_subjects: 'looked at the subjects',
  organise_subject: 'changed a subject',
  file_under_subject: 'filed something under a subject',
  list_appointments: 'looked at the timetable',
  organise_appointment: 'changed a timetable entry',
  write_writeup: 'wrote up a session',
  list_links: 'looked at what is linked',
  link_content: 'linked two things',
  'tools/list': 'asked what it can do',
  initialize: 'connected',
}

function when(moment: string): string {
  const at = new Date(moment)
  return Number.isNaN(at.getTime())
    ? moment
    : at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}
